// Lib
import React, { ReactElement } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Link } from '../../../../../node_module_clones/react-router';

// Components
import AccountPlanSection from './summary/AccountPlanSection';
import { GroupTranslatorProvider, useGroupTranslator } from '../../../../components/i18n/GroupTranslationContext';

// Selectors
import { getCurrentUserSubscription, userLocalePreferenceSelector } from '../../../currentUserSelector';

// Utils
import { isStripeSubscription } from '../../../../../common/payment/subscriptions/subscriptionUtils';
import {
    getSubscriptionPlanCancelAt,
    getSubscriptionPlanCancelAtPeriodEnd,
    getSubscriptionPlanName,
} from '../../../../../common/payment/subscriptionPropertyUtil';
import {
    CALENDAR_DATE_FORMAT_PRESETS,
    formatCalendarDate,
} from '../../../../../common/utils/datetime/calendarDateFormatterIntl';

import './AccountPlanContainer.scss';

const mapStateToProps = createStructuredSelector({
    subscription: getCurrentUserSubscription,
    userLocalePreference: userLocalePreferenceSelector,
});

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector> & {
    children: React.ReactElement;
    showSubscriptionDetails?: boolean;
    route?: { name: string };
    location: { pathname: string };
    userLocalePreference?: string;
};

const AccountPlanContainerContent = (props: Props): ReactElement => {
    const { children, subscription, showSubscriptionDetails = true, route, location, userLocalePreference } = props;
    const groupTranslate = useGroupTranslator();
    const translate = groupTranslate;

    if (
        subscription &&
        !isStripeSubscription(subscription) &&
        location.pathname !== '/account/plan/immediate-downgrade'
    ) {
        const cancelAtPeriodEnd = getSubscriptionPlanCancelAtPeriodEnd(subscription);
        const cancelAt = getSubscriptionPlanCancelAt(subscription);
        const cancelAtFormattedDate = cancelAt
            ? formatCalendarDate({
                  timestamp: cancelAt * 1000,
                  localePreference: userLocalePreference,
                  preset: CALENDAR_DATE_FORMAT_PRESETS.LONG_WITH_WEEKDAY,
              })
            : '';
        const planName = getSubscriptionPlanName(subscription);

        return (
            <div className="AccountPlanContainer">
                {route?.name && <h1>{route.name}</h1>}
                {showSubscriptionDetails && !cancelAtPeriodEnd && (
                    <AccountPlanSection
                        title={groupTranslate('container.currentPlan', { planName })}
                        action={<></>}
                    />
                )}
                {showSubscriptionDetails && cancelAtPeriodEnd && (
                    <AccountPlanSection
                        title={groupTranslate('container.cancelledPlan', { planName })}
                        details={groupTranslate('container.endSubscriptionText', { date: cancelAtFormattedDate })}
                        action={
                            <Link className="action LinkButton" to="/account/plan/immediate-downgrade">
                                {translate('container.endSubscriptionButton')}
                            </Link>
                        }
                    />
                )}
                <h3>
                    {translate('container.aboutSubscription')}
                </h3>
                <p className="account-subscription-warning">
                    {translate('container.appStoreWarning')}
                    {' '}
                    <a
                        href="https://help.milanote.com/en/articles/9147963-how-do-i-update-my-milanote-subscription-on-iphone-or-ipad"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {translate('container.learnMoreLink')}
                    </a>
                </p>
            </div>
        );
    }

    return children;
};

const AccountPlanContainer = (props: Props): ReactElement => {
    return (
        <GroupTranslatorProvider group="settings" keyPrefix="paymentBilling.plan">
            <AccountPlanContainerContent {...props} />
        </GroupTranslatorProvider>
    );
};

export default connector(AccountPlanContainer);
